import { Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import {
  ConfigService,
  FloatingElementDirective,
  FloatingMenuComponent,
  FloatingMenuItemComponent,
  HeaderComponent,
  MaterialIconComponent,
  NativeElementHook,
} from '@ps-gui/shared-core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProofBatchDetailsComponent } from '../../batch-details/proof-batch-details.component';
import { CtBatchApiService } from '../../batch-api/ct-batch-api.service';
import { ClaimsToastService } from '../../toast/claims-toast.service';
import { PsNavigationService } from '../../ps-navigation.service';
import { CtBatch, ProofCtBatch } from '../../model/ct-batch-model';
import { CtCommentButtonComponent } from './ct-comment-button.component';
import { AuditChatPopupDisplayComponent } from '../../audit-chat-display/audit-chat-popup-display.component';
import { AuditChatMessageService } from '../../audit-chat-display/audit-chat-message.service';
import { NgClass } from '@angular/common';
import { PageImageLoadingPrioritizer } from '../../batch-api/page-image-loading-prioritizer';
import { FormsModule } from '@angular/forms';

@Component({
  selector: '<ct-shared-header>',
  templateUrl: './ct-header.component.html',
  imports: [
    HeaderComponent,
    ProofBatchDetailsComponent,
    CtCommentButtonComponent,
    AuditChatPopupDisplayComponent,
    FloatingElementDirective,
    NativeElementHook,
    MaterialIconComponent,
    FloatingMenuComponent,
    FloatingMenuItemComponent,
    NgClass,
    FormsModule,
  ],
})
export class CtHeaderComponent {
  private readonly ctBatchApiService = inject(CtBatchApiService);
  private readonly toastService = inject(ClaimsToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly psNavigationService = inject(PsNavigationService);
  protected readonly auditChatMessageService = inject(AuditChatMessageService);
  protected readonly configService = inject(ConfigService);

  protected readonly derivedIsChatMessagePopupOpened = computed(() => {
    return this.hasChatMessagePopupBeenToggledManual()
      ? this.isChatMessagePopupOpened()
      : this.messages().length > 0;
  });
  protected readonly isChatMessagePopupOpened = signal(false);
  protected readonly hasChatMessagePopupBeenToggledManual = signal(false);

  protected readonly doMessagesExist = computed(() => this.messages().length > 0);

  protected readonly isSettingsToggled = signal(false);

  readonly ctBatch = input.required<CtBatch>();
  metadata!: Record<string, string>;

  setChatMessagePopupOpened(open: boolean): void {
    this.isChatMessagePopupOpened.set(open);
    this.hasChatMessagePopupBeenToggledManual.set(true);
  }

  onLogoClicked() {
    this.unlockItemAndOpenOrigin();
  }

  readonly _setMaxStoredImages = signal(100);
  readonly _setHalfToleratedWindow = signal(10);
  readonly _setHalfLoadWindow = signal(20);

  private readonly prioritizer = inject(PageImageLoadingPrioritizer);

  updatePrioritizerSettings() {
    this.prioritizer.reconfigure(
      this._setMaxStoredImages(),
      this._setHalfToleratedWindow(),
      this._setHalfLoadWindow(),
    );
  }

  private unlockItemAndOpenOrigin() {
    this.ctBatchApiService
      .unlockCtBatch(this.ctBatch().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.psNavigationService.navigateToOriginWithoutBack(this.ctBatch());
        },
        error: () => {
          this.toastService.error(`Vorgang ${this.ctBatch().id} konnte nicht entsperrt werden`);
        },
      });
  }

  protected readonly messages = computed(() =>
    [...this.auditChatMessageService.messages()].toSorted((a, b) =>
      a.created.localeCompare(b.created),
    ),
  );

  protected proofCtBatch() {
    // TODO-DOMAIN
    return this.ctBatch() as ProofCtBatch;
  }

  protected openProofForBatch() {
    window.open(`${this.configService.config.auditUrl}/audit/${this.proofCtBatch().auditId}`);
  }

  protected readonly window = window;
}
